import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Tooltip,
  Typography
} from "@mui/material";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "../firebase/auth";
import { db } from "../firebase/config";
import { Match } from "../types";

interface MatchWithId extends Match {
  id: string;
}

const AdminDashboard: FC = () => {
  const [matches, setMatches] = useState<MatchWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "match"), (snapshot) => {
      const data = snapshot.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Match),
      }));
      setMatches(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  const copyLink = (id: string) => {
    const url = `${window.location.origin}/match/${id}`;
    navigator.clipboard.writeText(url);
  };

  const handleReset = async () => {
    if (!resetTarget) return;
    const matchDoc = doc(db, "match", resetTarget);
    const match = matches.find((m) => m.id === resetTarget);
    if (!match) return;
    await updateDoc(matchDoc, {
      sillas: {},
      claimedAwards: {},
      local: { ...match.local, assignedSeats: 0 },
      visitante: { ...match.visitante, assignedSeats: 0 },
    });
    setResetTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteDoc(doc(db, "match", deleteTarget));
    setDeleteTarget(null);
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, bgcolor: "#1a1a2e", minHeight: "100vh" }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, bgcolor: "#1a1a2e", minHeight: "100vh" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" color="white">
          Partidos
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => navigate("/admin/match/new")}
          >
            Crear partido
          </Button>
          <Button variant="outlined" color="inherit" sx={{ color: "#fff" }} onClick={handleLogout}>
            Salir
          </Button>
        </Box>
      </Box>

      {matches.length === 0 && (
        <Typography color="grey.400">
          No hay partidos creados aún.
        </Typography>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {matches.map((match) => {
          const totalAssigned =
            match.local.assignedSeats + match.visitante.assignedSeats;
          const progress =
            match.totalSeats > 0
              ? (totalAssigned / match.totalSeats) * 100
              : 0;
          const totalAwards = Object.keys(match.hiddenAwards || {}).length;
          const claimedCount = Object.keys(match.claimedAwards || {}).length;

          return (
            <Card key={match.id}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6">
                      {match.local.teamName} vs {match.visitante.teamName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {match.stadiumName} — {match.typeGame}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
                      <Chip
                        label={`${totalAssigned} / ${match.totalSeats} asientos`}
                        size="small"
                        color={totalAssigned >= match.totalSeats ? "success" : "default"}
                      />
                      {totalAwards > 0 && (
                        <Chip
                          label={`${claimedCount} / ${totalAwards} premios reclamados`}
                          size="small"
                          color={claimedCount === totalAwards ? "warning" : "default"}
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(progress, 100)}
                      sx={{ mt: 1, width: 200 }}
                    />
                  </Box>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Tooltip title="Copiar enlace público">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => copyLink(match.id)}
                      >
                        Copiar link
                      </Button>
                    </Tooltip>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        navigate(`/admin/match/${match.id}/edit`)
                      }
                    >
                      Editar
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => setResetTarget(match.id)}
                    >
                      Reiniciar
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => setDeleteTarget(match.id)}
                    >
                      Eliminar
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Reset confirmation */}
      <Dialog open={!!resetTarget} onClose={() => setResetTarget(null)}>
        <DialogTitle>Reiniciar partido</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se borrarán todos los asientos seleccionados y los premios reclamados. Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)}>Cancelar</Button>
          <Button onClick={handleReset} color="warning" variant="contained">
            Reiniciar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Eliminar partido</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se eliminará el partido permanentemente. Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
